import React, { useState, useEffect, useRef } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './EditMessageTemplate.module.css';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import { Button, Tooltip } from '@mui/material';
import { FormatBold, FormatItalic } from '@mui/icons-material';
import { isWhatsAppPolicyError, getWhatsAppErrorMessage } from '../../utils/whatsappErrors';

// Cada variável disponível para uso nos templates, com um valor de exemplo
// (usado no preview ao vivo) e uma descrição curta (usada na lista de
// referência exibida ao usuário).
const TEMPLATE_VARIABLES = {
    name: { example: "João Silva", description: "Primeiro nome do cliente" },
    when: { example: "quinta-feira", description: "Dia da semana (ex.: segunda-feira)" },
    day: { example: "15", description: "Dia do mês" },
    month: { example: "março", description: "Mês por extenso" },
    time: { example: "14:30", description: "Horário do agendamento" },
    greetings: { example: "Bom dia", description: "Saudação (bom dia/boa tarde/boa noite)" },
    company_name: { example: "Empresa Exemplo", description: "Nome da empresa" },
    professional_name: { example: "Maria Santos", description: "Nome do profissional" },
    procedure_name: { example: "Avaliação Inicial", description: "Nome do serviço/procedimento" },
    address: { example: "Rua das Flores, 123", description: "Endereço da empresa" },
    company_phone: { example: "(11) 99999-0000", description: "Telefone de contato da empresa" },
    patient_name: { example: "João da Silva Santos", description: "Nome completo do cliente" },
    price_pix: { example: "180,00", description: "Valor para pagamento via Pix" },
    price_card: { example: "200,00", description: "Valor para pagamento no cartão" },
    company_policies: { example: "Cancelamentos com 24h de antecedência", description: "Política de cancelamento/atendimento da empresa" }
};

// Variáveis que não são mais oferecidas na lista, mas seguem preenchidas no
// preview (e no envio) para não quebrar modelos antigos. {deadline}: desde
// 2026-11-04 a mensagem da reserva não cita mais o prazo do cadastro.
const LEGACY_TEMPLATE_VARIABLES = {
    deadline: { example: "hoje, às 15:30" }
};

const EditMessageTemplate = ({ template, displayName, onUpdate, onHide }) => {
    const { UpdateMessageTemplate } = DataProviderInstance();
    const [content, setContent] = useState(template.TemplateContent);
    const [preview, setPreview] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const isFallback = template.isFallback == true;

    useEffect(() => {
        updatePreview(content);
    }, [content]);

    const escapeHtml = (str) => String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const updatePreview = (text) => {
        // Escapar o conteúdo digitado pelo usuário antes de qualquer inserção de HTML,
        // evitando XSS via tags/atributos digitados no template (ex.: <script>, onerror=...)
        let previewText = escapeHtml(text);

        // Substituir variáveis pelos valores de exemplo
        Object.entries({ ...TEMPLATE_VARIABLES, ...LEGACY_TEMPLATE_VARIABLES }).forEach(([key, { example }]) => {
            previewText = previewText.replace(new RegExp(`{${key}}`, 'g'), example);
        });

        // Converter quebras de linha \n para <br /> para exibição HTML
        previewText = previewText.replace(/\n/g, '<br />');
        
        // Processar formatação do WhatsApp
        // Negrito: *texto* -> <strong>texto</strong>
        previewText = previewText.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
        
        // Itálico: _texto_ -> <em>texto</em>
        previewText = previewText.replace(/_(.*?)_/g, '<em>$1</em>');
        
        setPreview(previewText);
    };

    const validateTemplate = (text) => {
        const requiredVariables = [
           // 'name', 'day', 'month', 'when', 'time', 'doctor_name',
            //'company_name', 'address', 'price_pix', 'price_card'
        ];

        const missingVariables = requiredVariables.filter(variable => {
            const isRequired = template.TemplateKey.includes('Appointment') || 
                             variable == 'name' || 
                             variable == 'company_name';
            return isRequired && !text.includes(`{${variable}}`);
        });

        if (missingVariables.length > 0) {
            setError(`Template deve incluir as seguintes variáveis: ${missingVariables.join(', ')}`);
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validateTemplate(content)) {
            return;
        }

        setSaving(true);
        setError('');

        try {
            // If this is a fallback template, we need to pass a special ID to indicate
            // that we want to create a new template instead of updating an existing one
            const templateId = isFallback ? `create:${template.TemplateKey}` : template.Id;
            
            await UpdateMessageTemplate(
                templateId,
                content,
                (response) => {
                    if (response.success) {
                        onUpdate();
                    } else if (response.success === false && isWhatsAppPolicyError(response)) {
                        setError(getWhatsAppErrorMessage(response));
                        setSaving(false);
                    } else {
                        setError('Falha ao salvar template');
                        setSaving(false);
                    }
                }
            );
        } catch (err) {
            setError('Erro ao salvar template: ' + err.message);
            setSaving(false);
        }
    };

    const textareaRef = useRef(null);
    
    const insertFormatting = (format) => {
        if (!textareaRef.current) return;
        
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = content.substring(start, end);
        
        let newContent = content;
        let newCursorPos;
        
        if (selectedText) {
            // Se há texto selecionado, envolva-o com a formatação
            const formatChar = format === 'bold' ? '*' : '_';
            const formattedText = `${formatChar}${selectedText}${formatChar}`;
            newContent = content.substring(0, start) + formattedText + content.substring(end);
            newCursorPos = start + formattedText.length;
        } else {
            // Se não há texto selecionado, insira os caracteres de formatação e posicione o cursor entre eles
            const formatChar = format === 'bold' ? '*' : '_';
            newContent = content.substring(0, start) + formatChar + formatChar + content.substring(end);
            newCursorPos = start + 1;
        }
        
        setContent(newContent);
        
        // Restaurar o foco e a posição do cursor após a atualização do estado
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const getContent = () => (
        <div className={styles.editorContainer}>
            <div className={styles.formattingTools}>
                <Tooltip title="Negrito (*texto*)">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => insertFormatting('bold')}
                        startIcon={<FormatBold />}
                    >
                        Negrito
                    </Button>
                </Tooltip>
                <Tooltip title="Itálico (_texto_)">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => insertFormatting('italic')}
                        startIcon={<FormatItalic />}
                    >
                        Itálico
                    </Button>
                </Tooltip>
                <div className={styles.formattingHelp}>
                    <span>Formatação: <strong>*negrito*</strong>, <em>_itálico_</em>, pressione Enter para quebrar linha</span>
                </div>
            </div>
            <div className={styles.editorGrid}>
                <div className={styles.editorSection}>
                    <h3>Editor</h3>
                    <textarea
                        ref={textareaRef}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        className={styles.editor}
                    />
                </div>
                <div className={styles.previewSection}>
                    <h3>Preview</h3>
                    <div
                        className={styles.preview}
                        dangerouslySetInnerHTML={{ __html: preview }}
                    />
                </div>
            </div>
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}
            <div className={styles.variablesHelp}>
                <h3>Variáveis Disponíveis</h3>
                <div className={styles.variablesGrid}>
                    {Object.entries(TEMPLATE_VARIABLES).map(([key, { description }]) => (
                        <div key={key} className={styles.variableItem}>
                            <code>{`{${key}}`}</code>
                            <span>{description}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const getPositiveButton = () => (
        <CustomButton
            variant="contained"
            style="primary"
            label={saving ? "Salvando..." : "Salvar Alterações"}
            onClick={handleSave}
            disabled={saving}
        />
    );

    return (
        <PopupBase
            Title={`Editar mensagem: ${displayName || template.TemplateKey}`}
            Content={getContent()}
            Footer={getPositiveButton()}
            OnClose={onHide}
        />
    );
};

export default EditMessageTemplate;